import { datadogLogs } from "@datadog/browser-logs";

type LogLevel = "debug" | "info" | "warn" | "error";
type LogContext = Record<string, unknown>;

const SENSITIVE_KEY = /(authorization|password|passcode|token|secret|cookie|email|phone|user.?id|device.?id)/i;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const BEARER_PATTERN = /Bearer\s+[^\s,]+/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const MAX_DEPTH = 5;
const CORRELATION_KEY = "qit:correlation-id";

const createId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `qit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const getCorrelationId = (): string => {
    if (typeof window === "undefined") return createId();
    try {
        const existing = sessionStorage.getItem(CORRELATION_KEY);
        if (existing) return existing;
        const created = createId();
        sessionStorage.setItem(CORRELATION_KEY, created);
        return created;
    } catch {
        return createId();
    }
};

const redactString = (value: string): string =>
    value
        .replace(JWT_PATTERN, "[REDACTED_TOKEN]")
        .replace(BEARER_PATTERN, "Bearer [REDACTED]")
        .replace(EMAIL_PATTERN, "[REDACTED_EMAIL]");

const redact = (value: unknown, depth = 0, seen = new WeakSet<object>()): unknown => {
    if (typeof value === "string") return redactString(value);
    if (value === null || value === undefined || typeof value === "number" || typeof value === "boolean") return value;
    if (value instanceof Error) {
        return { name: value.name, message: redactString(value.message), stack: import.meta.env.DEV ? value.stack : undefined };
    }
    if (depth >= MAX_DEPTH) return "[TRUNCATED]";
    if (typeof value !== "object") return String(value);
    if (seen.has(value)) return "[CIRCULAR]";
    seen.add(value);

    if (Array.isArray(value)) return value.slice(0, 50).map((item) => redact(item, depth + 1, seen));
    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .slice(0, 50)
            .map(([key, item]) => [key, SENSITIVE_KEY.test(key) ? "[REDACTED]" : redact(item, depth + 1, seen)]),
    );
};

const sampledEvents = new Map<string, number>();
const shouldSample = (key: string, intervalMs: number): boolean => {
    const now = Date.now();
    const lastSeen = sampledEvents.get(key) ?? 0;
    if (now - lastSeen < intervalMs) return false;
    sampledEvents.set(key, now);
    return true;
};

const emit = (level: LogLevel, event: string, context: LogContext = {}) => {
    if ((level === "debug" || level === "info") && !import.meta.env.DEV) return;

    const payload = redact({
        event,
        correlation_id: getCorrelationId(),
        ...context,
    }) as LogContext;

    if (import.meta.env.DEV) {
        const method = level === "debug" ? "debug" : level;
        originalConsole[method](`[${event}]`, payload);
        return;
    }

    if (level === "warn" || level === "error") {
        try {
            datadogLogs.logger[level](event, payload);
        } catch {
            originalConsole[level](`[${event}]`, payload);
        }
    }
};

const originalConsole = {
    debug: console.debug.bind(console),
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
};

const emittedBrowserDiagnostics = new Set<string>();

/** Temporary, redacted browser-console diagnostic that bypasses the production guard once per page load. */
export const browserDiagnosticOnce = (event: string, context: LogContext = {}) => {
    if (emittedBrowserDiagnostics.has(event)) return;
    emittedBrowserDiagnostics.add(event);
    originalConsole.error(`[${event}]`, redact(context));
};

export const logger = {
    debug: (event: string, context?: LogContext) => emit("debug", event, context),
    info: (event: string, context?: LogContext) => emit("info", event, context),
    warn: (event: string, context?: LogContext) => emit("warn", event, context),
    error: (event: string, error?: unknown, context?: LogContext) => emit("error", event, { ...context, error }),
    sampledDebug: (event: string, context?: LogContext, intervalMs = 10_000) => {
        if (shouldSample(event, intervalMs)) emit("debug", event, context);
    },
    sampledWarn: (event: string, context?: LogContext, intervalMs = 30_000) => {
        if (shouldSample(event, intervalMs)) emit("warn", event, context);
    },
    correlationId: getCorrelationId,
};

/** Protect production while legacy console call sites are migrated incrementally. */
export const installProductionConsoleGuard = () => {
    if (!import.meta.env.PROD) return;
    console.debug = () => undefined;
    console.log = () => undefined;
    console.info = () => undefined;
    console.warn = (...args: unknown[]) => emit("warn", "legacy.console.warn", { args });
    console.error = (...args: unknown[]) => emit("error", "legacy.console.error", { args });
};
